import { Feather, FontAwesome5, MaterialIcons } from "@expo/vector-icons";
import { LinearGradient } from "expo-linear-gradient";
import React, { useState, useEffect, useRef } from "react";
import {
  View,
  Text,
  Platform,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  Animated,
  Alert
} from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { useDispatch, useSelector } from "react-redux";
import { useQuery, useMutation } from "@tanstack/react-query";
import Fade from "react-native-fade";
import {
  BackButton,
  FloatingButton,
  Loading,
  StepHeader,
  Input,
  Centered,
} from "../../../components/all";
import { updateProfile, getProfile } from "../../../services/profile";
import { getProfessionsByType } from "../../../services/shared";
import { getLocalizedProfessionName, getLocalizedProfessionTypeName, extractProfessionTitleAndSubtitle } from "../../../utils/professionUtils";
import { addFields } from "../../../reducers/profile/profile";
import { normalize } from "../../../utils/normalize";
import Styles from "../../../constants/styles";
import colors from "../../../constants/colors";

const EditProfession = (props) => {
  const safeInsets = useSafeAreaInsets();
  const dispatch = useDispatch();
  const { fields } = useSelector((state) => state.profile);
  const { country } = useSelector((state) => state.user);

  const [selectedProfessions, setSelectedProfessions] = useState([]);
  const [filterInput, setFilterInput] = useState("");
  const [showError, setShowError] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const animatedValues = useRef({});
  const [animationsInitialized, setAnimationsInitialized] = useState(false);
  const [profileDataLoaded, setProfileDataLoaded] = useState(false);

  // Fetch profile data to ensure we have the latest professions
  const { data: profileData, isLoading: profileLoading } = useQuery({
    queryKey: ['editProfessionProfile'],
    queryFn: getProfile,
    onSuccess: (data) => {
      if (data?.data?.data) {
        dispatch(addFields(data.data.data));
        setProfileDataLoaded(true);
      }
    },
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
  });

  // Get profession type from user's profile (this is fixed, cannot be changed)
  const professionTypeCode = fields?.profession?.profession_type_code || profileData?.data?.data?.profession?.profession_type_code;
  const professionType = fields?.profession?.profession_type || profileData?.data?.data?.profession?.profession_type;

  // Fetch professions filtered by the user's fixed profession type
  const { data: professionsData, isLoading, error, refetch } = useQuery({
    queryKey: ['professions', country, professionTypeCode],
    queryFn: () => getProfessionsByType(country || 'MY', professionTypeCode),
    enabled: !!professionTypeCode,
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
    onError: (error) => {
      console.error('Failed to fetch professions:', error);
    }
  });

  const professions = professionsData?.data?.data || [];

  // Initialize selected professions from user's current professions
  useEffect(() => {
    // Use professions from either Redux store or fresh profile data
    const professions = fields?.professions || profileData?.data?.data?.professions;
    
    if (professions && Array.isArray(professions) && professions.length > 0) {
      // Ensure we have the full profession objects, not just IDs
      if (typeof professions[0] === 'object' && professions[0].id) {
        setSelectedProfessions(professions);
      }
    }
  }, [fields?.professions, profileData, profileDataLoaded]);

  // Filter professions based on search and sort alphabetically
  const filteredProfessions = professions
    .filter(profession => {
      if (!filterInput) return true;
      const name = getLocalizedProfessionName(profession, country);
      return name.toLowerCase().includes(filterInput.toLowerCase());
    })
    .sort((a, b) => {
      const nameA = getLocalizedProfessionName(a, country);
      const nameB = getLocalizedProfessionName(b, country);
      return nameA.localeCompare(nameB);
    });

  // Initialize animations for each profession
  useEffect(() => {
    if (!isLoading && filteredProfessions.length > 0 && !animationsInitialized) {
      filteredProfessions.forEach((profession) => {
        if (!animatedValues.current[profession.id]) {
          animatedValues.current[profession.id] = new Animated.Value(0);
        }
      });

      // Animate entrance
      const animations = filteredProfessions.map((profession, index) => {
        return Animated.timing(animatedValues.current[profession.id], {
          toValue: 1,
          duration: 400,
          delay: index * 50, // Stagger effect
          useNativeDriver: true,
        });
      });

      Animated.parallel(animations).start();
      setAnimationsInitialized(true);
    }
  }, [isLoading, filteredProfessions, animationsInitialized]);

  const toggleProfession = (profession) => {
    setShowError(false);

    // For multiple selection
    const isSelected = selectedProfessions.some(p => p.id === profession.id);
    if (isSelected) {
      // Remove from selection
      setSelectedProfessions(selectedProfessions.filter(p => p.id !== profession.id));
    } else {
      // Add to selection
      setSelectedProfessions([...selectedProfessions, profession]);
    }
  };

  const onUpdateProfessions = useMutation({
    mutationFn: updateProfile,
    onError: (error) => {
      setVerifying(false);
      Alert.alert(
        "Update Error",
        error.response?.data?.message || "Failed to update professions. Please try again."
      );
    },
    onSuccess: (res) => {
      setVerifying(false);
      // Update Redux state with new professions
      dispatch(addFields({
        professions: selectedProfessions,
        profession_ids: selectedProfessions.map(p => p.id),
        profession_id: selectedProfessions[0]?.id,
        expertise: selectedProfessions[0]?.id,
      }));

      props.navigation.navigate({
        name: "ProfileScreen",
        params: {
          profileEditSuccess: Math.random(),
        },
        merge: true,
      });
    },
  });

  const onPressUpdate = () => {
    if (selectedProfessions.length === 0) {
      setShowError(true);
      return;
    }

    setVerifying(true);

    // Prepare update data - supporting multiple professions
    const updateData = {
      professions: selectedProfessions,
      profession_ids: selectedProfessions.map(p => p.id),
      profession_id: selectedProfessions[0]?.id, // Keep first as primary for backward compatibility
      expertise: selectedProfessions[0]?.id, // Keep first as primary for backward compatibility
      scope: "professions"
    };

    onUpdateProfessions.mutate(updateData);
  };

  // Get icon and color based on profession code pattern
  const getIconConfig = (profession) => {
    const code = profession.code || '';
    if (code.includes('doc') || code.includes('physician')) {
      return { icon: 'medical-services', color: '#4B9BFF', bgColor: '#E3F2FF' };
    } else if (code.includes('surg')) {
      return { icon: 'healing', color: '#10B981', bgColor: '#D1FAE5' };
    } else if (code.includes('pedi')) {
      return { icon: 'child-care', color: '#F59E0B', bgColor: '#FEF3C7' };
    } else if (code.includes('card')) {
      return { icon: 'favorite', color: '#EF4444', bgColor: '#FEE2E2' };
    } else if (code.includes('neuro')) {
      return { icon: 'psychology', color: '#8B5CF6', bgColor: '#EDE9FE' };
    } else {
      return { icon: 'local-hospital', color: colors.primary, bgColor: colors.primary + '10' };
    }
  };

  // Show loading state
  if (isLoading || profileLoading) {
    return (
      <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: 'white' }}>
        <ActivityIndicator size="large" color={colors.primary} />
        <Text style={{ marginTop: 16 }}>Loading professions...</Text>
      </View>
    );
  }

  // Show error state
  if (error || !professionTypeCode) {
    return (
      <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', padding: 32, backgroundColor: 'white' }}>
        <Text style={{ fontSize: 16, textAlign: 'center', marginBottom: 16 }}>
          {!professionTypeCode
            ? "Unable to load your profession type. Please try again."
            : "Unable to load professions. Please check your internet connection and try again."}
        </Text>
        <TouchableOpacity
          onPress={() => professionTypeCode ? refetch() : props.navigation.goBack()}
          style={{
            backgroundColor: colors.primary,
            paddingHorizontal: 24,
            paddingVertical: 12,
            borderRadius: 8,
          }}
        >
          <Text style={{ color: 'white', fontWeight: 'bold' }}>
            {professionTypeCode ? "Retry" : "Go Back"}
          </Text>
        </TouchableOpacity>
      </View>
    );
  }

  const { title: professionTitle, subtitle: professionSubtitle } = extractProfessionTitleAndSubtitle(getLocalizedProfessionTypeName(professionType, country));

  return (
    <>
      <View style={{ backgroundColor: "white", flex: 1, ...Styles.webSmallContainer }}>
        <BackButton />
        <ScrollView
          showsVerticalScrollIndicator={false}
          contentContainerStyle={{ paddingBottom: 100 }}
        >
          <StepHeader
            label={`Edit\nProfession`}
            icon={<FontAwesome5 name="briefcase-medical" size={24} color="black" />}
            subtitle={`Select your professional specializations. You can choose multiple professions.`}
          />

          {/* Display fixed profession type */}
          {/* {professionType && (
            <View style={{
              paddingHorizontal: 20,
              marginTop: 20,
              marginBottom: 10,
              backgroundColor: '#f5f5f5',
              padding: 16,
              borderRadius: 12,
              marginHorizontal: 20,
            }}>
           
              <Text style={{
                fontSize: normalize(12),
                color: "rgba(0,0,0,0.5)",
                marginTop: 4,
              }}>
                {professions.length} profession{professions.length !== 1 ? 's' : ''} available
              </Text>
            </View>
          )} */}

          {(professionTitle || professionSubtitle) && (
            <View>
              {professionTitle ? (
                <Text style={{
                  fontSize: normalize(18),
                  fontWeight: "600",
                  color: colors.primary,
                  marginTop: 20,
                  marginLeft: 24,
                }}>
                  {professionTitle}
                </Text>
              ) : null}
              {professionSubtitle ? (
                <Text style={{
                  fontSize: normalize(14),
                  color: "rgba(0,0,0,0.5)",
                  marginTop: 4,
                  marginLeft: 24,
                  textTransform: "capitalize",
                }}>
                  {professionSubtitle}
                </Text>
              ) : null}
            </View>
          )}

          <View style={{ padding: 20 }}>
            <Input
              onChangeText={(text) => setFilterInput(text)}
              value={filterInput}
              placeholder={"Search professions"}
            />

            <Centered style={{ width: "100%" }}>
              <Fade visible={showError} duration={300}>
                <Text
                  style={{ fontSize: normalize(10), color: "red", marginTop: 10 }}
                >
                  Please select at least one profession
                </Text>
              </Fade>
            </Centered>

            {/* Professions List */}

            <View style={{ marginTop: 0 }}>
              {filteredProfessions.map((profession) => {
                const isSelected = selectedProfessions.some(p => p.id === profession.id);
                const animValue = animatedValues.current[profession.id] || new Animated.Value(1);
                const iconConfig = getIconConfig(profession);
                const professionName = getLocalizedProfessionName(profession, country);
                const { title, subtitle } = extractProfessionTitleAndSubtitle(professionName);
                if (!title) return null;

                return (
                  <Animated.View
                    key={profession.id}
                    style={{
                      opacity: animValue,
                      transform: [
                        {
                          translateY: animValue.interpolate({
                            inputRange: [0, 1],
                            outputRange: [20, 0],
                          }),
                        },
                        {
                          scale: isSelected ? 0.98 : 1,
                        },
                      ],
                    }}
                  >
                    <TouchableOpacity
                      onPress={() => toggleProfession(profession)}
                      activeOpacity={0.7}
                      style={{
                        marginBottom: 12,
                        padding: 18,
                        borderRadius: 20,
                        borderWidth: 2,
                        borderColor: isSelected ? iconConfig.color : "#F3F4F6",
                        backgroundColor: "white",
                        flexDirection: 'row',
                        alignItems: 'center',
                        shadowColor: "#000",
                        shadowOffset: {
                          width: 0,
                          height: 4,
                        },
                        shadowOpacity: isSelected ? 0.15 : 0.08,
                        shadowRadius: 12,
                        elevation: 3,
                      }}
                    >
                      {/* Checkbox */}
                      <View
                        style={{
                          width: 20,
                          height: 20,
                          borderRadius: 6,
                          borderWidth: 2,
                          borderColor: isSelected ? iconConfig.color : "#D1D5DB",
                          backgroundColor: isSelected ? iconConfig.color : "white",
                          marginRight: 12,
                          justifyContent: 'center',
                          alignItems: 'center',
                        }}
                      >
                        {isSelected && (
                          <MaterialIcons
                            name="check"
                            size={14}
                            color="white"
                          />
                        )}
                      </View>

                      <View style={{ flex: 1 }}>
                        <Text style={{
                          fontSize: normalize(14),
                          color: isSelected ? iconConfig.color : "#1F2937",
                          fontWeight: isSelected ? "600" : "500",
                          letterSpacing: -0.3,
                        }}>
                          {title}
                        </Text>
                        {subtitle && (
                          <Text style={{
                            fontSize: normalize(11),
                            color: isSelected ? iconConfig.color + '99' : "rgba(0, 0, 0, 0.5)",
                            fontWeight: "400",
                            marginTop: 2,
                            letterSpacing: -0.2,
                          }}>
                            {subtitle}
                          </Text>
                        )}
                      </View>

                    </TouchableOpacity>
                  </Animated.View>
                );
              })}
            </View>

            {filteredProfessions.length === 0 && (
              <Centered style={{ marginTop: 40 }}>
                <Text style={{
                  fontSize: normalize(14),
                  color: "rgba(0,0,0,0.5)",
                  textAlign: 'center',
                }}>
                  No professions found matching "{filterInput}"
                </Text>
              </Centered>
            )}
          </View>
        </ScrollView>
      </View>

      <FloatingButton status={"rejected"} onPress={onPressUpdate}>
        <View
          style={{
            borderRadius: 100,
            overflow: "hidden",
          }}
        >
          <LinearGradient
            start={{ x: -0.2, y: 0.2 }}
            colors={[
              "rgb(248, 100, 108)",
              "rgb(242, 49, 123)",
              "rgb(234,0,137)",
            ]}
          >
            <View
              style={{
                height: 70,
                width: 70,
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Feather name={"check-circle"} size={30} color="white" />
            </View>
          </LinearGradient>
        </View>
      </FloatingButton>

      {Platform.OS !== "web" ? (
        <Loading visible={verifying} />
      ) : null}
    </>
  );
};

export default EditProfession;